import { record } from "rrweb";
import { SDK_DEFAULTS, type WireEvent } from "@watchover/shared";
import type { WatchOverConfig } from "./config.js";

/**
 * Session-replay recorder. Wraps rrweb and coalesces its high-frequency event
 * stream into periodic `replay` envelopes so we make one transport call every
 * couple of seconds rather than per frame.
 *
 * Privacy default: ALL input values are masked in the replay stream (only their
 * presence/shape is recorded, not the typed text), matching FullStory/PostHog
 * defaults. Customers opt specific fields back in via unmaskSelectors. Text
 * nodes are additionally masked via .wo-mask / [data-wo-mask] / maskSelectors,
 * and whole subtrees dropped via .wo-block.
 */
export class ReplayRecorder {
  private records: unknown[] = [];
  private stopFn: (() => void) | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private cfg: WatchOverConfig,
    private emit: (event: WireEvent) => void,
  ) {}

  start() {
    try {
      this.stopFn =
        record({
          emit: (event) => {
            this.records.push(event);
            // Hard cap so a runaway page can't balloon memory before the timer.
            if (this.records.length >= 200) this.flush();
          },
          // Mask every input by default; unmaskSelectors opt specific fields in.
          maskAllInputs: true,
          maskInputOptions: { password: true },
          maskTextClass: "wo-mask",
          blockClass: "wo-block",
          ignoreClass: "wo-ignore",
          // Also mask text nodes explicitly tagged for redaction, plus any the
          // customer named. [data-wo-mask] previously did nothing in replay.
          maskTextSelector:
            ["[data-wo-mask]", ".ph-no-capture", ...this.cfg.maskSelectors].join(
              ",",
            ) || undefined,
          recordCanvas: false,
          collectFonts: false,
          // Don't reach into cross-origin iframes (pin against rrweb defaults).
          recordCrossOriginIframes: false,
          // Throttle the noisiest signals; "last" keeps only the final input value.
          sampling: { mousemove: 50, scroll: 150, input: "last" },
          // Periodic full snapshot bounds replay-start cost for long sessions.
          checkoutEveryNms: 5 * 60_000,
        }) ?? null;
    } catch (err) {
      if (this.cfg.debug) console.warn("[watchover] replay start failed", err);
      return;
    }
    this.timer = setInterval(() => this.flush(), SDK_DEFAULTS.replayFlushMs);
  }

  /** Force an rrweb full snapshot (e.g. right after an SPA route change). */
  snapshot() {
    try {
      record.takeFullSnapshot(true);
    } catch {
      /* recorder not active */
    }
  }

  flush() {
    if (!this.records.length) return;
    const records = this.records;
    this.records = [];
    this.emit({ t: "replay", ts: Date.now(), records });
  }

  stop() {
    this.flush();
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.stopFn?.();
    this.stopFn = null;
  }
}
